/**
 * LegalUpdates Widget (F-003)
 * Dashboard component showing upcoming legal/regulatory changes
 */

import React, { useEffect, useState } from 'react';
import { complianceService } from '../../../services/api';

interface LegalUpdate {
    id: string;
    title: string;
    description: string;
    effectiveDate: string;
    severity: 'critical' | 'important' | 'info';
    category: string;
    source?: string;
}

const LegalUpdates: React.FC = () => {
    const [updates, setUpdates] = useState<LegalUpdate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchUpdates = async () => {
            try {
                const response = await complianceService.getLegalUpdates();
                const data = response.data?.data || response.data || [];
                setUpdates(Array.isArray(data) ? data : []);
            } catch (err: any) {
                console.error('[LegalUpdates] Fetch error:', err);
                if (err.response && err.response.status === 403) {
                    setError('ACCESS_DENIED');
                } else {
                    setError('Failed to load legal updates');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUpdates();
    }, []);

    const getSeverityBadge = (severity: string) => {
        const styles = {
            critical: 'bg-red-100 text-red-800 border-red-200',
            important: 'bg-amber-100 text-amber-800 border-amber-200',
            info: 'bg-blue-100 text-blue-800 border-blue-200'
        };

        const labels = {
            critical: '⚠️ Critical',
            important: '📢 Important',
            info: 'ℹ️ Info'
        };

        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[severity as keyof typeof styles] || styles.info}`}>
                {labels[severity as keyof typeof labels] || labels.info}
            </span>
        );
    };

    const getDaysUntil = (dateStr: string) => {
        const target = new Date(dateStr);
        const now = new Date();
        const diffMs = target.getTime() - now.getTime();
        const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return days;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-3">
                    <div className="h-16 bg-gray-100 rounded"></div>
                    <div className="h-16 bg-gray-100 rounded"></div>
                </div>
            </div>
        );
    }

    if (error === 'ACCESS_DENIED') {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <i className="fas fa-gavel text-indigo-500 mr-2"></i>
                    Legal Updates
                </h3>
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg text-center">
                    <i className="fas fa-lock text-indigo-400 text-xl mb-2"></i>
                    <p className="text-indigo-900 font-medium text-sm">Unlock Legal Updates</p>
                    <p className="text-indigo-700 text-xs mt-1">Upgrade to Pro to get real-time regulatory alerts.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal Updates</h3>
                <p className="text-red-500 text-sm">{error}</p>
            </div>
        );
    }

    if (updates.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <i className="fas fa-gavel text-green-500 mr-2"></i>
                    Legal Updates
                </h3>
                <div className="text-center py-6 text-gray-500">
                    <i className="fas fa-check-circle text-green-500 text-2xl mb-2"></i>
                    <p>No upcoming regulatory changes</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <i className="fas fa-gavel text-indigo-500 mr-2"></i>
                Legal Updates
                <span className="ml-2 text-xs font-normal text-gray-500">({updates.length})</span>
            </h3>

            <div className="space-y-4">
                {updates.slice(0, 5).map((update) => {
                    const daysUntil = getDaysUntil(update.effectiveDate);
                    const isUrgent = daysUntil <= 30;

                    return (
                        <div
                            key={update.id}
                            className={`p-4 rounded-lg border ${isUrgent ? 'border-amber-200 bg-amber-50' : 'border-gray-100 bg-gray-50'}`}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-gray-900 text-sm flex-1 pr-2">
                                    {update.title}
                                </h4>
                                {getSeverityBadge(update.severity)}
                            </div>

                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                {update.description}
                            </p>

                            <div className="flex items-center justify-between text-xs">
                                <span className={`font-medium ${isUrgent ? 'text-amber-700' : 'text-gray-500'}`}>
                                    <i className="fas fa-calendar-alt mr-1"></i>
                                    {daysUntil > 0
                                        ? `Effective in ${daysUntil} days`
                                        : daysUntil === 0
                                            ? 'Effective today!'
                                            : `Effective ${Math.abs(daysUntil)} days ago`
                                    }
                                </span>

                                {update.source && (
                                    <a
                                        href={update.source}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800"
                                    >
                                        View source <i className="fas fa-external-link-alt ml-1"></i>
                                    </a>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {updates.length > 5 && (
                <div className="mt-4 text-center">
                    <button className="text-sm text-indigo-600 hover:text-indigo-800">
                        View all {updates.length} updates →
                    </button>
                </div>
            )}
        </div>
    );
};

export default LegalUpdates;
